import { PieChart as RechartsPie, Pie, Cell, Legend, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, AreaChart, Area } from "recharts";

/* ─── Theme Colors (from screenshots) ─── */
const PURPLE_GRADIENT = ["#2563eb", "#2563eb"]; // Solid blue (bar chart)
const DONUT_COLORS = ["#2563eb", "#3b82f6", "#60a5fa", "#93c5fd", "#1d4ed8", "#1e40af", "#2563eb", "#3b82f6"];
const TEAL_LINE = "#2563eb"; // Blue for area chart
const TEAL_FILL = "rgba(37, 99, 235, 0.08)";

/* ─── Static Data ─── */
const STATUS_DIST = { OPEN: 3, ESCALATED: 2, RESOLVED: 3 };
const EXCEPTION_TYPES = { MISSING_METER_READ: 12, EXCESSIVE_BILL: 9, ZERO_NEGATIVE_BILL: 7, RATE_DETERMINATION_FAILED: 5, ZERO_CONSUMPTION: 4 };
const URGENCY = { HIGH: 6, MEDIUM: 2 };
const WORK_ORDER_TYPES = { URGENT_BILLING_CORRECTION: 1, METER_DIAGNOSTIC: 1, HIGH_BILL_INVESTIGATION: 1, BILLING_AUDIT: 1, FIELD_METER_INSPECTION: 1, INVOICE_CORRECTION: 1, USAGE_ANALYSIS: 1, FIRMWARE_UPDATE: 1 };

const FUNNEL_DATA = [
  { name: "Total Cases", value: 8 },
  { name: "Investigated", value: 5 },
  { name: "Work Order Created", value: 8 },
  { name: "Escalated", value: 2 },
  { name: "Resolved", value: 3 },
];

const TREND_DATA = [
  { date: "Jun 1", cases: 5 },
  { date: "Jun 3", cases: 8 },
  { date: "Jun 5", cases: 12 },
  { date: "Jun 7", cases: 6 },
  { date: "Jun 9", cases: 10 },
  { date: "Jun 11", cases: 15 },
  { date: "Jun 13", cases: 9 },
  { date: "Jun 15", cases: 20 },
  { date: "Jun 17", cases: 45 },
  { date: "Jun 18", cases: 34 },
];

function M2CDashboard() {
  const totalCases = TREND_DATA.reduce((s, d) => s + d.cases, 0);

  return (
    <>
      {/* Row 1: 3 cards — Status Donut, Exception Types Bar, Urgency Donut */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {/* 1. Status Distribution — Donut style (screenshot 2 theme) */}
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-1">Status Distribution</h3>
          <p className="text-xs text-gray-400 mb-4">Case status breakdown</p>
          <DonutCard data={STATUS_DIST} colorMap={{ OPEN: "#60a5fa", ESCALATED: "#1d4ed8", RESOLVED: "#2563eb" }} />
        </div>

        {/* 2. Cases by Exception Type — Purple gradient bar (screenshot 1 theme) */}
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-1">Cases by Exception Type</h3>
          <p className="text-xs text-gray-400 mb-4">This period</p>
          <PurpleBarChart data={EXCEPTION_TYPES} />
        </div>

        {/* 3. Urgency Distribution — Donut style */}
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-1">Urgency Distribution</h3>
          <p className="text-xs text-gray-400 mb-4">Priority breakdown</p>
          <DonutCard data={URGENCY} colorMap={{ HIGH: "#1d4ed8", MEDIUM: "#60a5fa", LOW: "#93c5fd" }} />
        </div>
      </section>

      {/* Row 2: Escalation Funnel + Case Trend */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Escalation Funnel */}
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-1">Escalation Funnel</h3>
          <p className="text-xs text-gray-400 mb-4">Case pipeline</p>
          <div className="space-y-3 pt-2">
            {FUNNEL_DATA.map((item, i) => {
              const maxVal = FUNNEL_DATA[0].value || 1;
              const pct = Math.max((item.value / maxVal) * 100, 10);
              const opacity = 1 - i * 0.15;
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div
                      className="h-8 rounded-lg flex items-center px-3 transition-all"
                      style={{
                        width: `${pct}%`,
                        background: "#2563eb",
                        opacity,
                        minWidth: "40px",
                      }}
                    >
                      <span className="text-white text-xs font-bold">{item.value}</span>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 font-medium min-w-[110px]">{item.name}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Case Trend Over Time */}
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-6">
          <div className="flex items-start justify-between mb-1">
            <div>
              <h3 className="text-base font-semibold text-gray-800">Case Trend Over Time</h3>
              <p className="text-3xl font-bold text-gray-900 mt-1">{totalCases}</p>
              <p className="text-sm text-blue-600 font-medium mt-0.5">+{TREND_DATA[TREND_DATA.length - 1].cases} new · Jun 2026</p>
            </div>
            <span className="text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full border border-gray-100">Last 18 days</span>
          </div>
          <div className="w-full h-[220px] mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={TREND_DATA} margin={{ left: 0, right: 10, top: 10, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontSize: "12px" }} />
                <Area type="monotone" dataKey="cases" stroke="#2563eb" strokeWidth={2.5} fill="rgba(37, 99, 235, 0.08)" dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff", fill: "#2563eb" }} name="Cases" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>
    </>
  );
}

/* ─── Donut Card (Screenshot 2 theme: thick ring, labels with lines, bottom legend) ─── */
function DonutCard({ data, colorMap }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const sum = entries.reduce((s, [, v]) => s + v, 0);
  if (sum === 0) return <p className="text-xs text-gray-400">No data</p>;
  const chartData = entries.map(([label, value], idx) => ({
    name: label.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    value,
    color: colorMap?.[label] || DONUT_COLORS[idx % DONUT_COLORS.length],
  }));

  const renderLabel = ({ cx, cy, midAngle, outerRadius, value, index }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 20;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const pct = ((value / sum) * 100).toFixed(0);
    return (
      <text x={x} y={y} fill="#6b7280" textAnchor={x > cx ? "start" : "end"} dominantBaseline="central" fontSize={10} fontWeight={500}>
        {value} ({pct}%)
      </text>
    );
  };

  return (
    <div className="w-full h-[240px]">
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPie>
          <Pie data={chartData} cx="50%" cy="45%" outerRadius={75} innerRadius={45} dataKey="value" nameKey="name" label={renderLabel} labelLine={{ stroke: "#d1d5db", strokeWidth: 1 }}>
            {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
          </Pie>
          <Tooltip formatter={(v, n) => [`${v} (${((v / sum) * 100).toFixed(1)}%)`, n]} />
          <Legend layout="horizontal" align="center" verticalAlign="bottom" iconType="square" iconSize={10} wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }} />
        </RechartsPie>
      </ResponsiveContainer>
    </div>
  );
}

/* ─── Purple Gradient Bar Chart (Screenshot 1 theme: rounded, gradient fill, no grid) ─── */
function PurpleBarChart({ data }) {
  const barData = Object.entries(data)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, count]) => ({
      name: name.replace(/_/g, " "),
      count,
    }));
  if (barData.length === 0) return <p className="text-xs text-gray-400 text-center py-8">No data</p>;
  return (
    <div className="w-full h-[300px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={barData} margin={{ left: 10, right: 10, top: 5, bottom: 90 }}>
          <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#1f2937" }} axisLine={false} tickLine={false} angle={-40} textAnchor="end" height={100} interval={0} />
          <YAxis tick={{ fontSize: 10, fill: "#1f2937" }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontSize: "12px" }} formatter={(v, name, props) => [`${v} case${v > 1 ? "s" : ""}`, props.payload.name]} />
          <Bar dataKey="count" fill="#2563eb" radius={[6, 6, 0, 0]} barSize={22} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default M2CDashboard;
